import {
    CosmeticPageState,
    CosmeticPageErrors,
    ReducerActionNames,
    CosmeticProcessStages
} from './CosmeticRepairRequestTypes';
import { SelectInputHelper } from '../../common/selectInput/SelectInput';
import { UploadImageTypes } from '../../../Constants/Common';
import { UploadedImage, AddressType } from '../../common/CommonTypes';
import { Panels } from './CosmeticRepairRequestTypes';

type Action =
    | { type: 'update-cosmetic-errors'; cosmeticErrors: CosmeticPageErrors }
    | {
          type: 'update-cosmetic-date';
          key: string;
          value: string;
      }
    | { type: 'set-has-errors'; hasErrors: boolean }
    | { type: 'update-mileage'; mileage: string }
    | { type: 'update-incident-occured'; incidentOccured: string }
    | { type: 'update-damage-type'; key: string; value: string }
    | { type: 'update-damage-depth'; damageDepth: string }
    | { type: 'update-damage-length'; damageLength: string }
    | { type: 'save-cosmetic-request' }
    | { type: 'update-uploaded-images'; uploadedImages: UploadedImage[] }
    | { type: 'add-cosmetic-request'; productReference: string }
    | { type: 'update-cosmetic-stage'; newStage: CosmeticProcessStages }
    | { type: 'set-repair-address-selected'; value: number }
    | { type: 'set-repair-address'; address: AddressType }
    | {
          type: 'update-cosmetic-request';
          requestId: number;
          productReference: string;
      }
    | {
          type: 'update-selected-panels';
          selected: boolean;
          panel: Panels;
      }
    | { type: 'scroll-to-address'; scrollToAddress: boolean }
    | { type: 'show-repair-request-form'; show: boolean }
    | {
          type: 'set-loading-request-submission';
          loadingOnRequestSubmission: boolean;
      }
    | { type: 'set-permission-granted'; permissionGranted: boolean }
    | { type: 'show-alert'; showCancelAlert: boolean }
    | {
          type: 'delete-cosmetic-request';
          requestId: number;
      };

const CosmeticRepairRequestReducer = (
    state: CosmeticPageState,
    action: Action
): CosmeticPageState => {
    switch (action.type) {
        case ReducerActionNames.SetHasErrors:
            return {
                ...state,
                hasErrors: action.hasErrors
            };
        case ReducerActionNames.UpdateCosmeticErrors:
            return {
                ...state,
                cosmeticFormError: action.cosmeticErrors
            };
        case ReducerActionNames.UpdateCosmeticDate: {
            const updatedCurrentRepairRequest = {
                ...state.currentRepairRequest,
                [action.key]: action.value
            };

            return {
                ...state,
                currentRepairRequest: updatedCurrentRepairRequest
            };
        }
        case ReducerActionNames.UpdateMileage: {
            const updatedCurrentRepairRequest = {
                ...state.currentRepairRequest,
                mileage: action.mileage
            };
            return {
                ...state,
                currentRepairRequest: updatedCurrentRepairRequest
            };
        }
        case ReducerActionNames.UpdateIncidentOccured: {
            const updatedCurrentRepairRequest = {
                ...state.currentRepairRequest,
                incidentOccured: action.incidentOccured
            };
            return {
                ...state,
                currentRepairRequest: updatedCurrentRepairRequest
            };
        }
        case ReducerActionNames.UpdateDamageType: {
            const updatedCurrentRepairRequest = {
                ...state.currentRepairRequest,
                [action.key]: action.value
            };
            return {
                ...state,
                currentRepairRequest: updatedCurrentRepairRequest
            };
        }
        case ReducerActionNames.SaveCosmeticRequest: {
            const repairRequestIndex = state.cosmeticRepairRequests.findIndex(
                (row) => row.id === state.currentRepairRequest.id
            );
            if (repairRequestIndex < 0) {
                const updatedRepairRequest = {
                    ...state.currentRepairRequest,
                    requestNumber: state.cosmeticRepairRequests.length + 1
                };
                return {
                    ...state,
                    showRepairRequestForm: false,
                    currentRepairRequest: updatedRepairRequest,
                    cosmeticRepairRequests: [
                        ...state.cosmeticRepairRequests,
                        updatedRepairRequest
                    ]
                };
            } else {
                return {
                    ...state,
                    showRepairRequestForm: false,
                    cosmeticRepairRequests: state.cosmeticRepairRequests.map(
                        (crr) =>
                            crr.id === state.currentRepairRequest.id
                                ? { ...state.currentRepairRequest }
                                : { ...crr }
                    )
                };
            }
        }
        case ReducerActionNames.UpdateCosmeticRequest: {
            const repairRequestIndex = state.cosmeticRepairRequests.findIndex(
                (row) => row.id === action.requestId
            );
            if (repairRequestIndex > -1) {
                return {
                    ...state,
                    cosmeticStage: CosmeticProcessStages.General,
                    showRepairRequestForm: true,
                    currentRepairRequest:
                        state.cosmeticRepairRequests[repairRequestIndex],
                    repairRequestSelectedIndex: repairRequestIndex
                };
            } else {
                return createNewRepairRequest(state, action.productReference);
            }
        }
        case ReducerActionNames.DeleteCosmeticRequest: {
            const newRepairRequest = state.cosmeticRepairRequests.filter(
                (row) => row.id !== action.requestId
            );

            const repairRequestIndex = newRepairRequest.length - 1;

            return {
                ...state,
                cosmeticRepairRequests: newRepairRequest,
                repairRequestSelectedIndex: repairRequestIndex,
                currentRepairRequest: newRepairRequest[repairRequestIndex]
            };
        }

        case ReducerActionNames.UpdateDamageDepth: {
            const updatedCurrentRepairRequest = {
                ...state.currentRepairRequest,
                damageDepth: action.damageDepth
            };
            return {
                ...state,
                currentRepairRequest: updatedCurrentRepairRequest
            };
        }
        case ReducerActionNames.UpdateUploadedImages: {
            return {
                ...state,
                currentRepairRequest: {
                    ...state.currentRepairRequest,
                    uploadedImages: action.uploadedImages
                }
            };
        }
        case ReducerActionNames.UpdateDamageLength: {
            const updatedCurrentRepairRequest = {
                ...state.currentRepairRequest,
                damageLength: action.damageLength
            };
            return {
                ...state,
                currentRepairRequest: updatedCurrentRepairRequest
            };
        }

        case ReducerActionNames.AddCosmeticRequest:
            return createNewRepairRequest(state, action.productReference);
        case ReducerActionNames.UpdateCosmeticStage:
            return {
                ...state,
                cosmeticStage: action.newStage
            };
        case ReducerActionNames.UpdateRepairAddressSelected: {
            return {
                ...state,
                repairAddressSelectedIndex: action.value
            };
        }
        case ReducerActionNames.SetRepairAddress: {
            return {
                ...state,
                repairAddresses: [...state.repairAddresses, action.address]
            };
        }
        case ReducerActionNames.UpdateSelectedPanels: {
            if (action.selected === true) {
                //insert the new selected panel
                return {
                    ...state,
                    currentRepairRequest: {
                        ...state.currentRepairRequest,
                        selectedPanels: [
                            ...state.currentRepairRequest.selectedPanels,
                            action.panel
                        ]
                    }
                };
            }
            //remove the selected panel
            return {
                ...state,
                currentRepairRequest: {
                    ...state.currentRepairRequest,
                    selectedPanels:
                        state.currentRepairRequest.selectedPanels.filter(
                            (p) => p !== action.panel
                        )
                }
            };
        }
        case ReducerActionNames.ScrollToAddress: {
            return {
                ...state,
                scrollToAddress: action.scrollToAddress
            };
        }
        case ReducerActionNames.ShowRepairRequestForm: {
            return {
                ...state,
                showRepairRequestForm: action.show
            };
        }
        case ReducerActionNames.LoadingOnRequestSubmission: {
            return {
                ...state,
                isLoadingOnSubmission: action.loadingOnRequestSubmission
            };
        }
        case ReducerActionNames.SetPermissionGranted: {
            return {
                ...state,
                permissionGranted: action.permissionGranted
            };
        }
        case ReducerActionNames.ShowCancelAlert: {
            return {
                ...state,
                showCancelAlert: action.showCancelAlert
            };
        }
        default:
            return state;
    }
};

const createNewRepairRequest = (
    state: CosmeticPageState,
    productReference: string
): CosmeticPageState => {
    const newIndex = state.cosmeticRepairRequests.length;

    const newRepairRequest = {
        id: newIndex,
        productReference: productReference,
        dateOfIncident: undefined,
        mileage: '',
        incidentOccured: '',
        damageType: SelectInputHelper.NullValue(),
        damageDepth: SelectInputHelper.NullValue(),
        damageLength: SelectInputHelper.NullValue(),
        requestNumber: '',
        uploadedImages: [
            {
                type: UploadImageTypes.CloseUp,
                uploadedPath: ''
            },
            {
                type: UploadImageTypes.Distance,
                uploadedPath: ''
            },
            {
                type: UploadImageTypes.AdditionalImage1,
                uploadedPath: ''
            },
            {
                type: UploadImageTypes.AdditionalImage2,
                uploadedPath: ''
            }
        ],
        selectedPanels: []
    };

    return {
        ...state,
        showRepairRequestForm: true,
        repairRequestSelectedIndex: newIndex,
        currentRepairRequest: newRepairRequest
    };
};

export default CosmeticRepairRequestReducer;
